'use client';

/**
 * ModelPickerMenu
 *
 * A compact, secondary affordance in the composer toolbar (immediately right of
 * the "+" menu) that lets the user switch which chat model Meridian uses. The
 * catalog is SERVER-DRIVEN via `useModelPreference` — the hook never hardcodes
 * model ids, and the server already gates who sees DeepSeek options (admins
 * only), so this component needs no role logic of its own.
 *
 * Selecting a row persists the choice (or clears back to the system default);
 * the hook fires a window event so other mounted instances (e.g. the settings
 * modal) resync. Kept quiet per MARKETING_MINIMALISM: one primary action (pick a
 * model), progressive disclosure (descriptions are muted/xs), restrained chrome.
 */

import React, { memo, useCallback } from 'react';
import { Check, Cpu } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@wtc/ui/components/popover';
import { useModelPreference } from '../../../hooks/useModelPreference';

export interface ModelPickerMenuProps {
  /** Disable the trigger (e.g. while the composer itself is disabled). */
  disabled?: boolean;
  /** Meridian API base — passed through to the preference hook. */
  apiBaseUrl?: string;
  /** Extra classes for the trigger button. */
  className?: string;
}

const TRIGGER_CLASS =
  'inline-flex h-8 max-w-[10rem] items-center gap-1.5 rounded-full px-2.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50';

interface ModelRowProps {
  label: string;
  description: string;
  caveat?: string;
  checked: boolean;
  disabled: boolean;
  onSelect: () => void;
}

function ModelRow({ label, description, caveat, checked, disabled, onSelect }: ModelRowProps) {
  return (
    <button
      type="button"
      role="menuitemradio"
      aria-checked={checked}
      disabled={disabled}
      onClick={onSelect}
      className="flex w-full items-start gap-2 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60"
    >
      <span className="mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center">
        {checked && <Check className="h-4 w-4 text-primary" aria-hidden />}
      </span>
      <span className="min-w-0">
        <span className="block truncate text-sm font-medium text-foreground">{label}</span>
        <span className="block text-xs text-muted-foreground">{description}</span>
        {caveat && (
          <span className="mt-0.5 block text-xs text-amber-600 dark:text-amber-400">{caveat}</span>
        )}
      </span>
    </button>
  );
}

function ModelPickerMenuComponent({ disabled = false, apiBaseUrl, className }: ModelPickerMenuProps) {
  const [open, setOpen] = React.useState(false);
  const { models, selectedModelId, systemDefault, activeModelLabel, isLoading, isSaving, saveModelId } =
    useModelPreference(apiBaseUrl ? { apiBaseUrl } : {});

  const select = useCallback(
    async (modelId: string | null) => {
      await saveModelId(modelId);
      setOpen(false);
    },
    [saveModelId]
  );

  const noModelsYet = isLoading && models.length === 0;
  const triggerDisabled = disabled || noModelsYet;

  // Which row is currently active: the explicit pick, else the system default.
  const activeId = selectedModelId ?? systemDefault?.modelId ?? null;
  const systemDefaultChecked = selectedModelId === null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={triggerDisabled}
          className={className ? `${TRIGGER_CLASS} ${className}` : TRIGGER_CLASS}
          aria-label="Choose chat model"
          title="Choose chat model"
        >
          <Cpu className="h-4 w-4 flex-shrink-0" aria-hidden />
          <span className="truncate">{activeModelLabel ?? 'Model'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" side="top" sideOffset={8} className="w-72 p-1.5">
        {models.length === 0 ? (
          <p className="px-2.5 py-2 text-sm text-muted-foreground">Model list unavailable right now.</p>
        ) : (
          <>
            <ModelRow
              label="System default"
              description={systemDefault ? systemDefault.label : 'Use the configured default'}
              checked={systemDefaultChecked}
              disabled={isSaving}
              onSelect={() => void select(null)}
            />
            {models.map((m) => (
              <ModelRow
                key={m.id}
                label={m.label}
                description={m.description}
                caveat={m.provider === 'deepseek' ? 'External · data leaves WTC' : undefined}
                checked={!systemDefaultChecked && activeId === m.id}
                disabled={isSaving}
                onSelect={() => void select(m.id)}
              />
            ))}
          </>
        )}
      </PopoverContent>
    </Popover>
  );
}

export const ModelPickerMenu = memo(ModelPickerMenuComponent);
